import { useMemo } from 'react'
import { toast } from 'sonner'
import type { QuoteInfo } from '../../quotes/types'
import { ActionDropdown } from '@shared/components/ui/action-dropdown/ActionDropdown'
import { Button } from '@shared/components/ui/button'
import { getQuotePanelActions, type QuoteActionHandlers } from '@shared/lib/actions/quote-actions'
import type { Quote } from '@shared/types/quotes'
import type { TableActionGroup } from '@shared/types/data-table'

const defaultActions = getQuotePanelActions({}) as unknown as TableActionGroup<QuoteInfo>[]

interface QuoteActionsProps {
  entityInfo: QuoteInfo
}

export function QuoteActionsComponent({ entityInfo }: QuoteActionsProps) {
  const primaryCTA = useMemo(() => {
    const status = entityInfo.status
    if (status === 'draft') return { label: 'Send quote' }
    if (status === 'sent') return { label: 'Mark as signed' }
    return null
  }, [entityInfo.status])

  return (
    <div className="flex items-center gap-2">
      {primaryCTA && (
        <Button
          variant="action"
          size="action"
          onClick={() => toast.info(`${primaryCTA.label} for quote`)}
        >
          {primaryCTA.label}
        </Button>
      )}
      <ActionDropdown actions={defaultActions} item={entityInfo} variant="outline" />
    </div>
  )
}

export function createQuoteActionsComponent(handlers: QuoteActionHandlers) {
  return function QuoteActionsWithHandlers({ entityInfo }: QuoteActionsProps) {
    const actions = getQuotePanelActions(handlers) as unknown as TableActionGroup<QuoteInfo>[]

    const primaryCTA = useMemo(() => {
      const status = entityInfo.status
      if (status === 'draft') return { label: 'Send quote', handler: () => handlers.onSendQuote?.(entityInfo as unknown as Quote) }
      if (status === 'sent') return { label: 'Mark as signed', handler: () => handlers.onMarkAsSigned?.(entityInfo as unknown as Quote) }
      return null
    }, [entityInfo])

    return (
      <div className="flex items-center gap-2">
        {primaryCTA && (
          <Button
            variant="action"
            size="action"
            onClick={() => primaryCTA.handler?.()}
          >
            {primaryCTA.label}
          </Button>
        )}
        <ActionDropdown actions={actions} item={entityInfo} variant="outline" />
      </div>
    )
  }
}
